import { useState, useEffect, useRef, useCallback } from "react";
import { Monitor, Tablet, Smartphone, Undo2, RotateCcw, Save, Globe, Eye, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ImageField } from "../../components/admin/fields";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const STYLE_FIELDS = [
  ["color", "Text Color", "color"],
  ["background-color", "Background", "color"],
  ["font-size", "Font Size (px)", "px"],
  ["letter-spacing", "Letter Spacing", "select", ["normal", "-0.02em", "0.05em", "0.1em", "0.2em"]],
  ["line-height", "Line Height", "select", ["normal", "1.2", "1.5", "1.75", "2"]],
  ["font-weight", "Font Weight", "select", ["300", "400", "500", "600", "700"]],
  ["text-align", "Align", "select", ["left", "center", "right"]],
  ["text-transform", "Transform", "select", ["none", "uppercase", "lowercase", "capitalize"]],
  ["opacity", "Opacity (0–1)", "text"],
  ["padding", "Padding", "text"],
  ["margin", "Margin", "text"],
  ["border-radius", "Corner Radius", "text"],
  ["border", "Border (e.g. 1px solid #000)", "text"],
  ["box-shadow", "Shadow (e.g. 0 4px 20px rgba(0,0,0,0.2))", "text"],
  ["width", "Width", "text"],
  ["max-width", "Max Width", "text"],
  ["min-height", "Min Height", "text"],
  ["object-fit", "Image Fit", "select", ["cover", "contain", "fill"]],
  ["object-position", "Image Position", "select", ["center", "top", "bottom", "left", "right"]],
];

const FIELDS_BY_TYPE = {
  text: ["color", "font-size", "font-weight", "text-align", "letter-spacing", "line-height", "text-transform", "opacity", "padding", "margin", "max-width"],
  button: ["color", "background-color", "font-size", "font-weight", "text-align", "padding", "border", "border-radius", "box-shadow", "letter-spacing", "opacity", "margin"],
  image: ["object-fit", "object-position", "border-radius", "opacity", "border", "box-shadow", "width", "min-height"],
  section: ["background-color", "padding", "margin", "min-height", "text-align", "border", "border-radius"],
};

export default function AdminWebsiteEditor() {
  const { refresh } = useTheme();
  const [overrides, setOverrides] = useState(null);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [device, setDevice] = useState("desktop");
  const [hasDraft, setHasDraft] = useState(false);
  const [hasBackup, setHasBackup] = useState(false);
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    api.get("/admin/editor/state").then((r) => {
      setOverrides(r.data.draft?.element_overrides || r.data.published_overrides || {});
      setHasDraft(!!r.data.draft);
      setHasBackup(r.data.has_backup);
    }).catch(() => setOverrides({}));

    const onMsg = (e) => {
      if (e.data?.source === "hc-editor" && e.data.type === "select") {
        setSelected({ eid: e.data.eid, etype: e.data.etype, text: e.data.text });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const post = useCallback((msg) => {
    iframeRef.current?.contentWindow?.postMessage({ source: "hc-editor-parent", ...msg }, "*");
  }, []);

  const update = useCallback((eid, patch) => {
    setOverrides((prev) => {
      const next = { ...prev, [eid]: { ...(prev[eid] || {}), ...patch } };
      setHistory((h) => [...h.slice(-19), prev]);
      post({ type: "apply", eid, override: next[eid] });
      return next;
    });
  }, [post]);

  const setStyle = (eid, prop, value) => {
    setOverrides((prev) => {
      const cur = prev[eid] || {};
      const styles = { ...(cur.styles || {}) };
      if (value) styles[prop] = value; else delete styles[prop];
      const next = { ...prev, [eid]: { ...cur, styles } };
      setHistory((h) => [...h.slice(-19), prev]);
      post({ type: "apply", eid, override: next[eid] });
      return next;
    });
  };

  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setOverrides(prev);
      post({ type: "applyAll", overrides: prev });
      return h.slice(0, -1);
    });
  };

  const resetElement = () => {
    if (!selected) return;
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[selected.eid];
      setHistory((h) => [...h.slice(-19), prev]);
      post({ type: "applyAll", overrides: next });
      return next;
    });
    toast.success("Element reset to default");
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put("/admin/editor/draft", { element_overrides: overrides });
      setHasDraft(true);
      toast.success("Draft saved — not live yet");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await api.post("/admin/editor/publish", { element_overrides: overrides });
      setHasDraft(false);
      setHasBackup(true);
      await refresh();
      toast.success("Published — changes are now live");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const discardDraft = async () => {
    try {
      await api.delete("/admin/editor/draft");
      const { data } = await api.get("/admin/editor/state");
      setOverrides(data.published_overrides || {});
      setHasDraft(false);
      post({ type: "applyAll", overrides: data.published_overrides || {} });
      toast.success("Draft discarded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async () => {
    try {
      const { data } = await api.post("/admin/editor/restore");
      setOverrides(data.element_overrides || {});
      post({ type: "applyAll", overrides: data.element_overrides || {} });
      await refresh();
      toast.success("Restored previous published version");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (overrides === null) return <div className="h-40 bg-muted animate-pulse" />;

  const cur = selected ? overrides[selected.eid] || {} : {};
  const fields = selected ? FIELDS_BY_TYPE[selected.etype] || FIELDS_BY_TYPE.text : [];
  const widths = { desktop: "100%", tablet: "768px", mobile: "390px" };

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="visual-editor">
      <div className="bg-[#111] text-[#FAF9F6] flex items-center gap-3 px-4 h-14 shrink-0">
        <p className="font-serif text-sm mr-2">Website <span className="text-gold">Editor</span></p>
        <div className="flex gap-1">
          {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
            <button key={d} onClick={() => setDevice(d)} className={`p-2 transition-colors duration-300 ${device === d ? "text-gold" : "text-[#A3A3A3] hover:text-white"}`} data-testid={`device-${d}`} aria-label={d}>
              <Icon strokeWidth={1.5} size={18} />
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {hasDraft && <span className="text-[10px] uppercase tracking-widest text-gold" data-testid="draft-badge">Unpublished draft</span>}
        <button onClick={undo} disabled={!history.length} className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white disabled:opacity-30 px-2" data-testid="editor-undo"><Undo2 strokeWidth={1.5} size={15} /> Undo</button>
        {hasBackup && <button onClick={restore} className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white px-2" data-testid="editor-restore"><RotateCcw strokeWidth={1.5} size={15} /> Restore</button>}
        {hasDraft && <button onClick={discardDraft} className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-destructive px-2" data-testid="editor-discard"><Trash2 strokeWidth={1.5} size={15} /> Discard</button>}
        <a href="/?preview=1" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white px-2" data-testid="editor-preview"><Eye strokeWidth={1.5} size={15} /> Preview</a>
        <button onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 border border-white/30 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold" data-testid="editor-save-draft"><Save strokeWidth={1.5} size={14} /> Save Draft</button>
        <button onClick={publish} disabled={saving} className="flex items-center gap-1.5 bg-gold text-[#111] px-4 py-2 text-xs uppercase tracking-widest font-medium hover:bg-white transition-colors duration-300" data-testid="editor-publish"><Globe strokeWidth={1.5} size={14} /> Publish</button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 bg-[#E8E4DC] flex justify-center overflow-auto p-4">
          <iframe
            ref={iframeRef}
            src="/?editor=1"
            title="Website preview"
            className="bg-white shadow-xl border border-black/10 h-full transition-all duration-300"
            style={{ width: widths[device], maxWidth: "100%" }}
            data-testid="editor-iframe"
          />
        </div>

        <div className="w-80 bg-white border-l border-black/10 overflow-y-auto shrink-0 p-5" data-testid="inspector-panel">
          {!selected ? (
            <div className="text-center pt-20">
              <p className="font-serif text-xl">Select an element</p>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">Hover over the preview — elements highlight in gold. Click any heading, text, button, image or section to edit it here.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="small-caps text-gold">{selected.etype}</p>
                <button onClick={resetElement} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive" data-testid="reset-element">Reset</button>
              </div>
              <p className="text-xs text-muted-foreground mb-5 font-mono">{selected.eid}</p>

              {["text", "button"].includes(selected.etype) && (
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-widest">Text Content</Label>
                  <textarea
                    value={cur.text !== undefined ? cur.text : selected.text}
                    onChange={(e) => update(selected.eid, { text: e.target.value })}
                    rows={3}
                    className="mt-2 w-full border border-input px-3 py-2 text-sm outline-none focus:border-gold resize-y"
                    data-testid="inspector-text"
                  />
                </div>
              )}
              {selected.etype === "button" && (
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-widest">Button Link</Label>
                  <Input value={cur.href || ""} onChange={(e) => update(selected.eid, { href: e.target.value })} placeholder="/shop" className="mt-2" data-testid="inspector-href" />
                </div>
              )}
              {selected.etype === "image" && (
                <div className="mb-4 flex flex-col gap-4">
                  <ImageField label="Replace Image" value={cur.src || ""} onChange={(v) => update(selected.eid, { src: v })} testid="inspector-image" />
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Alt Text</Label>
                    <Input value={cur.alt || ""} onChange={(e) => update(selected.eid, { alt: e.target.value })} className="mt-2" data-testid="inspector-alt" />
                  </div>
                </div>
              )}

              <p className="small-caps text-muted-foreground mb-3 mt-2">Style</p>
              <div className="flex flex-col gap-3">
                {fields.map((key) => {
                  const f = STYLE_FIELDS.find(([k]) => k === key);
                  if (!f) return null;
                  const [prop, label, kind, options] = f;
                  const val = cur.styles?.[prop] || "";
                  if (kind === "color") {
                    return (
                      <div key={prop} className="flex items-center gap-2">
                        <input type="color" value={val || "#111111"} onChange={(e) => setStyle(selected.eid, prop, e.target.value)} className="w-9 h-8 border border-black/10 cursor-pointer" data-testid={`style-${prop}`} />
                        <span className="text-xs flex-1">{label}</span>
                        {val && <button onClick={() => setStyle(selected.eid, prop, "")} className="text-[10px] text-muted-foreground hover:text-destructive">clear</button>}
                      </div>
                    );
                  }
                  if (kind === "select") {
                    return (
                      <div key={prop}>
                        <Label className="text-xs uppercase tracking-widest">{label}</Label>
                        <Select value={val || " "} onValueChange={(v) => setStyle(selected.eid, prop, v === " " ? "" : v)}>
                          <SelectTrigger className="mt-1 h-8 text-sm rounded-sm" data-testid={`style-${prop}`}><SelectValue placeholder="Default" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value=" ">Default</SelectItem>
                            {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  return (
                    <div key={prop}>
                      <Label className="text-xs uppercase tracking-widest">{label}</Label>
                      <Input value={val} onChange={(e) => setStyle(selected.eid, prop, e.target.value)} className="mt-1 h-8 text-sm" data-testid={`style-${prop}`} />
                    </div>
                  );
                })}
              </div>

              <p className="small-caps text-muted-foreground mb-3 mt-6">Visibility</p>
              <div className="flex flex-col gap-2">
                <label className="flex items-center justify-between text-sm">
                  Hidden <Switch checked={!!cur.hidden} onCheckedChange={(v) => update(selected.eid, { hidden: v })} data-testid="vis-hidden" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  Hide on Mobile <Switch checked={!!cur.hideMobile} onCheckedChange={(v) => update(selected.eid, { hideMobile: v })} data-testid="vis-mobile" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  Hide on Desktop <Switch checked={!!cur.hideDesktop} onCheckedChange={(v) => update(selected.eid, { hideDesktop: v })} data-testid="vis-desktop" />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
